import React, { useState, useEffect } from "react"
import { VStack, Text } from "@chakra-ui/react"
import Modal from "components/dialogs/Modal"
import Button from "components/Buttons/Button"
import {
  BadgeTemplate,
  PrinterModel,
  PrinterMedia,
} from "sharedTypes/badge"
import { errorToast } from "utilities/toasts"
import { validateTemplateDimensions, calculatePrintInstructions } from "components/BadgeBuilder/utils/badgeHelpers"
import PrinterLabelSelectionForm from "components/BadgeBuilder/PrinterLabelSelectionForm"

type Props = {
  isOpen: boolean
  onClose: () => void
  template: BadgeTemplate
  onTemplateChange: (
    updates: Partial<Pick<BadgeTemplate, "name" | "widthMm" | "heightMm" | "printerModel" | "printerMedia" | "printerOrientation">>
  ) => void
}

const EditPrinterLabelModal = ({
  isOpen,
  onClose,
  template,
  onTemplateChange,
}: Props) => {
  // State for form values
  const [printerModel, setPrinterModel] = useState<PrinterModel>(template.printerModel)
  const [printerMedia, setPrinterMedia] = useState<PrinterMedia | "">(template.printerMedia)

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setPrinterModel(template.printerModel)
      setPrinterMedia(template.printerMedia)
    }
  }, [isOpen, template])

  const handleSave = () => {
    // Media selection is required
    if (!printerMedia) {
      return
    }

    try {
      // Create a temporary template for validation
      const tempTemplate: BadgeTemplate = {
        ...template,
        printerModel,
        printerMedia,
      }

      const validation = validateTemplateDimensions(tempTemplate)

      if (!validation.valid) {
        errorToast({
          title: "Invalid Configuration",
          description: validation.error,
        })

        return
      }

      // Calculate new print instructions for the new printer/media combination
      const newPrintInstructions = calculatePrintInstructions({
        widthMm: template.widthMm,
        heightMm: template.heightMm,
        printerModel,
        printerMedia,
      })

      // Update the template properties
      onTemplateChange({
        printerModel,
        printerMedia,
        printerOrientation: newPrintInstructions,
      })
      onClose()
    } catch (error) {
      // Handle invalid printer/media combinations
      errorToast({
        title: "Invalid Printer/Media Combination",
        description: "This printer model is not compatible with the selected media type. Please choose a different combination.",
      })
    }
  }

  const handleCancel = () => {
    // Reset to original values
    setPrinterModel(template.printerModel)
    setPrinterMedia(template.printerMedia)
    onClose()
  }

  // Check if anything has changed
  const hasChanges = printerModel !== template.printerModel
    || printerMedia !== template.printerMedia

  const actions = [
    <Button variant="outline" colorScheme="button.light" onClick={handleCancel}>
      Cancel
    </Button>,
    <Button
      onClick={handleSave}
      isDisabled={!hasChanges || !printerMedia}
    >
      Save
    </Button>,
  ]

  return (
    <Modal
      show={isOpen}
      hide={handleCancel}
      title="Printer Model & Media"
      size="lg"
      actions={actions}
    >
      <VStack spacing={6} align="stretch">
        <Text color="gray.600">
          Select the printer model and media type for the &quot;{template.name}&quot; badge.
        </Text>

        <PrinterLabelSelectionForm
          printerModel={printerModel}
          printerMedia={printerMedia}
          onPrinterModelChange={setPrinterModel}
          onPrinterMediaChange={setPrinterMedia}
        />
      </VStack>
    </Modal>
  )
}

export default EditPrinterLabelModal
